// ========== CUSTOM STUDY MODAL - GRAIA DEFINITIVO ==========
// Verde Militar + Split-View + Power-Up Bar + Ícones Lucide

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Target,
    X,
    CheckSquare,
    Check,
    BookOpen,
    Layers,
    BarChart3,
    Play,
    AlertCircle
} from 'lucide-react';
import './CustomStudyModal.css';

interface StudyItem {
    _id: string;
    name: string;
    description?: string;
    cardCount?: number;
    subdeckCount?: number;
    isCustomDeck?: boolean;
    groupId?: string | null;
}

interface Subdeck {
    _id: string;
    name: string;
    cardCount: number;
}

interface CustomStudyModalProps {
    show: boolean;
    onClose: () => void;
    mode: 'decks' | 'subdecks';
    contextId: string;
    contextName: string;
    isCustomDeck?: boolean;
    prefetchedItems?: any[] | null;
    prefetchedSubdecksMap?: Map<string, any[]> | null;
    autoSelectAll?: boolean;
    groups?: Array<{ _id: string; name: string; color?: string }>;
}

const CustomStudyModal: React.FC<CustomStudyModalProps> = ({
    show,
    onClose,
    mode,
    contextId,
    contextName,
    isCustomDeck = false,
    prefetchedItems,
    prefetchedSubdecksMap,
    autoSelectAll = false,
    groups,
}) => {
    const navigate = useNavigate();
    const [items, setItems] = useState<StudyItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCards, setTotalCards] = useState(0);

    // Para modo DECKS
    const [decksSubdecksMap, setDecksSubdecksMap] = useState<Map<string, Subdeck[]>>(new Map());
    const [selectedSubdeckIds, setSelectedSubdeckIds] = useState<Set<string>>(new Set());
    const [activeDeckId, setActiveDeckId] = useState<string | null>(null);

    // Para modo DECKS — rastrear quais são custom decks
    const [customDeckIds, setCustomDeckIds] = useState<Set<string>>(new Set()); // ← NOVO

    // Para modo SUBDECKS
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // Confirmação de saída
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    // ========== EFEITOS ==========

    useEffect(() => {
        if (show) {
            // ✅ Se tem cache, usa direto — sem loading, sem fetch
            if (prefetchedItems && prefetchedItems.length > 0) {
                setItems(prefetchedItems);

                if (mode === 'decks') {
                    const customIds = new Set<string>(
                        prefetchedItems
                            .filter((d: any) => d.isCustomDeck === true)
                            .map((d: any) => d._id)
                    );
                    setCustomDeckIds(customIds);
                }

                if (prefetchedSubdecksMap) {
                    setDecksSubdecksMap(prefetchedSubdecksMap);
                }
                setLoading(false);
                if (autoSelectAll && mode === 'subdecks') {
                    setSelectedIds(prefetchedItems.map((i: any) => i._id));
                } else {
                    setSelectedIds([]);
                }
                setSelectedSubdeckIds(new Set());
                setActiveDeckId(null);
                return;
            }

            // Sem cache: fluxo normal
            loadItems();
            setSelectedIds([]);
            setSelectedSubdeckIds(new Set());
            setDecksSubdecksMap(new Map());
            setCustomDeckIds(new Set());
            setActiveDeckId(null);
        }
        // eslint-disable-next-line
    }, [show, contextId]);

    useEffect(() => {
        calculateTotalCards();
        // eslint-disable-next-line
    }, [selectedSubdeckIds, selectedIds, decksSubdecksMap]);

    // ========== CARREGAMENTO ==========
    const loadItems = async () => {
        // ✅ Usar cache ANTES de qualquer loading — abre instantaneamente
        if (prefetchedItems && prefetchedItems.length > 0) {
            setItems(prefetchedItems);

            // ← NOVO
            if (mode === 'decks') {
                const customIds = new Set<string>(
                    prefetchedItems
                        .filter((d: any) => d.isCustomDeck === true)
                        .map((d: any) => d._id)
                );
                setCustomDeckIds(customIds);
            }

            if (prefetchedSubdecksMap) {
                setDecksSubdecksMap(prefetchedSubdecksMap);
            }
            setLoading(false);
            return;
        }

        // Sem cache: fluxo normal com loading
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');

            if (mode === 'decks') {
                // 1. BUSCAR DECKS OFICIAIS DO CICLO
                const officialResponse = await fetch(`/api/cycles/${contextId}/decks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                let officialDecks: StudyItem[] = [];
                if (officialResponse.ok) {
                    const data = await officialResponse.json();
                    const decksArray = data.decks || data;
                    officialDecks = decksArray
                        .filter((deck: any) => deck.purchased === true)
                        .map((item: any) => ({
                            _id: item._id,
                            name: item.name,
                            description: item.description || '',
                            subdeckCount: item.subdeckCount || item.subdecks?.length || 0,
                            isCustomDeck: false, // ← NOVO
                        }));
                }

                // 2. BUSCAR CUSTOM DECKS DO CICLO
                const customResponse = await fetch(`/api/custom-decks/public/${contextId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                let customDecks: StudyItem[] = [];
                if (customResponse.ok) {
                    const customData = await customResponse.json();

                    customDecks = customData
                        .filter((deck: any) => deck.purchased === true || deck.isFree === true)
                        .map((item: any) => ({
                            _id: item._id,
                            name: item.name,
                            description: item.description || '',
                            subdeckCount: item.subdeckCount || 0,
                            isCustomDeck: true, // ← NOVO
                        }));
                }

                // 3. COMBINAR AMBOS
                const allDecks = [...officialDecks, ...customDecks];
                setItems(allDecks);

                // ← NOVO: guardar IDs dos custom decks
                setCustomDeckIds(new Set(customDecks.map((d) => d._id)));

                // 4. CARREGAR SUBDECKS DE TODOS
                await loadAllSubdecks(allDecks);

            } else if (mode === 'subdecks') {
                // ← MODIFICADO: usar endpoint correto baseado na prop isCustomDeck
                const endpoint = isCustomDeck
                    ? `/api/custom-decks/${contextId}/subdecks-meta`
                    : `/api/decks/${contextId}/subdecks-meta`;

                const response = await fetch(endpoint, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    const subdecksArray = data.subdecks || data;
                    const itemsData = subdecksArray
                        .filter((item: any) => item.type !== 'apostila')
                        .map((item: any) => ({
                            _id: item._id,
                            name: item.name,
                            description: item.description || '',
                            cardCount: item.cardCount || 0,
                        }));
                    setItems(itemsData);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAllSubdecks = async (decks: StudyItem[]) => {
        const token = localStorage.getItem('accessToken');
        const newMap = new Map<string, Subdeck[]>();

        for (const deck of decks) {
            try {
                let response: Response;

                // ← MODIFICADO: usar endpoint correto baseado em isCustomDeck do item
                if (deck.isCustomDeck) {
                    response = await fetch(`/api/custom-decks/${deck._id}/subdecks-meta`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } else {
                    response = await fetch(`/api/decks/${deck._id}/subdecks-meta`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    // Fallback
                    if (!response.ok) {
                        response = await fetch(`/api/custom-decks/${deck._id}/subdecks-meta`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }
                }

                if (response.ok) {
                    const data = await response.json();
                    const subdecks = (data.subdecks || data)
                        .filter((s: any) => !s.isBlocked && s.type !== 'apostila')
                        .map((s: any) => ({
                            _id: s._id,
                            name: s.name,
                            cardCount: s.cardCount || s.cards?.length || 0,
                        }));

                    newMap.set(deck._id, subdecks);
                    console.log(`✅ [MODAL] Subdecks carregados para "${deck.name}": ${subdecks.length}`);
                } else {
                    console.error(`❌ [MODAL] Erro ao carregar subdecks de ${deck.name}:`, response.status);
                }
            } catch (error) {
                console.error(`❌ [MODAL] Erro ao carregar subdecks do deck ${deck._id}:`, error);
            }
        }

        setDecksSubdecksMap(newMap);
    };

    const calculateTotalCards = () => {
        if (mode === 'subdecks') {
            const total = items
                .filter(item => selectedIds.includes(item._id))
                .reduce((sum, item) => sum + (item.cardCount || 0), 0);
            setTotalCards(total);
        } else if (mode === 'decks') {
            let total = 0;
            selectedSubdeckIds.forEach(subdeckId => {
                decksSubdecksMap.forEach(subdecks => {
                    const subdeck = subdecks.find(s => s._id === subdeckId);
                    if (subdeck) {
                        total += subdeck.cardCount;
                    }
                });
            });
            setTotalCards(total);
        }
    };

    // ========== HANDLERS ==========

    const handleToggleSubdeck = (subdeckId: string) => {
        setSelectedSubdeckIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(subdeckId)) {
                newSet.delete(subdeckId);
            } else {
                newSet.add(subdeckId);
            }
            return newSet;
        });
    };

    const handleToggle = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (mode === 'subdecks') {
            if (selectedIds.length === items.length) {
                setSelectedIds([]);
            } else {
                setSelectedIds(items.map(item => item._id));
            }
        } else if (mode === 'decks') {
            const allSubdeckIds = new Set<string>();
            decksSubdecksMap.forEach(subdecks => {
                subdecks.forEach(s => allSubdeckIds.add(s._id));
            });

            if (selectedSubdeckIds.size === allSubdeckIds.size) {
                setSelectedSubdeckIds(new Set());
            } else {
                setSelectedSubdeckIds(allSubdeckIds);
            }
        }
    };

    const handleSelectAllSubdecksOfDeck = (deckId: string) => {
        const subdecks = decksSubdecksMap.get(deckId) || [];
        const subdeckIds = subdecks.map(s => s._id);
        const allSelected = subdeckIds.every(id => selectedSubdeckIds.has(id));

        setSelectedSubdeckIds(prev => {
            const newSet = new Set(prev);
            if (allSelected) {
                subdeckIds.forEach(id => newSet.delete(id));
            } else {
                subdeckIds.forEach(id => newSet.add(id));
            }
            return newSet;
        });
    };

    const handleStartStudy = async () => {
        if (mode === 'subdecks') {
            if (selectedIds.length === 0) {
                alert('Selecione pelo menos um subdeck');
                return;
            }

            // ← MODIFICADO: incluir isCustomDeck no pool
            const studyPool = selectedIds.map(subdeckId => ({
                cycleId: '',
                deckId: contextId,
                subdeckId: subdeckId,
                isCustomDeck: isCustomDeck, // ← CRÍTICO: o hook usa isso para chamar o endpoint correto
            }));

            localStorage.setItem('customStudyPool', JSON.stringify(studyPool));
            localStorage.setItem('customStudyTotalCards', totalCards.toString());
            sessionStorage.setItem('graia_theme_context', 'enem');
            navigate('/study/custom');

        } else if (mode === 'decks') {
            if (selectedSubdeckIds.size === 0) {
                alert('Selecione pelo menos um subdeck');
                return;
            }

            const studyPool: any[] = [];

            decksSubdecksMap.forEach((subdecks, deckId) => {
                subdecks.forEach(subdeck => {
                    if (selectedSubdeckIds.has(subdeck._id)) {
                        studyPool.push({
                            cycleId: contextId,
                            deckId: deckId,
                            subdeckId: subdeck._id,
                            isCustomDeck: customDeckIds.has(deckId), // ← NOVO: identificar custom decks
                        });
                    }
                });
            });

            localStorage.setItem('customStudyPool', JSON.stringify(studyPool));
            localStorage.setItem('customStudyTotalCards', totalCards.toString());
            sessionStorage.setItem('graia_theme_context', 'enem');
            navigate('/study/custom');
        }
    };

    const handleCloseAttempt = () => {
        const hasSelection = mode === 'subdecks'
            ? selectedIds.length > 0
            : selectedSubdeckIds.size > 0;

        if (hasSelection) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };

    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onClose();
    };

    // ========== RENDER ==========

    if (!show) return null;

    const renderSplitView = () => {
        if (mode !== 'decks') return null;

        const activeDeck = items.find(d => d._id === activeDeckId);
        const activeSubdecks = activeDeckId ? decksSubdecksMap.get(activeDeckId) || [] : [];
        const selectedCount = activeSubdecks.filter(s => selectedSubdeckIds.has(s._id)).length;

        return (
            <div className="csm-split-container">
                {/* LEFT SIDEBAR - NAVEGAÇÃO DE DECKS */}
                <div className="csm-nav-sidebar">
                    <div className="csm-nav-list">
                        {items.map(deck => {
                            const isActive = activeDeckId === deck._id;
                            const subdecks = decksSubdecksMap.get(deck._id) || [];
                            const selected = subdecks.filter(s => selectedSubdeckIds.has(s._id)).length;

                            return (
                                <button
                                    key={deck._id}
                                    className={`csm-nav-deck-item ${isActive ? 'active' : ''}`}
                                    onClick={() => setActiveDeckId(deck._id)}
                                >
                                    <div className="csm-nav-deck-icon">
                                        <Layers size={20} />
                                    </div>
                                    <div className="csm-nav-deck-info">
                                        <div className="csm-nav-deck-name">{deck.name}</div>
                                        <div className="csm-nav-deck-count">
                                            {selected}/{deck.subdeckCount} selecionados
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT PANEL - SUBDECKS DO DECK ATIVO */}
                <div className="csm-content-panel">
                    {activeDeck ? (
                        <>
                            <div className="csm-panel-header">
                                <h3 className="csm-content-title">
                                    {activeDeck.name}
                                </h3>

                                <div className="csm-panel-actions">
                                    <button
                                        className="csm-btn-select-all"
                                        onClick={() =>
                                            handleSelectAllSubdecksOfDeck(activeDeck._id)
                                        }
                                    >
                                        <CheckSquare size={16} />
                                        {selectedCount === activeSubdecks.length
                                            ? 'Desmarcar todos'
                                            : 'Selecionar todos'}
                                    </button>

                                    <span className="csm-selection-counter">
                                        {selectedCount}/{activeSubdecks.length}
                                    </span>
                                </div>
                            </div>

                            <div className="csm-content">
                                <div className="csm-subdecks-grid">
                                    {activeSubdecks.map(subdeck => {
                                        const isSelected = selectedSubdeckIds.has(subdeck._id);

                                        return (
                                            <div
                                                key={subdeck._id}
                                                className={`csm-subdeck-grid-item ${isSelected ? 'selected' : ''}`}
                                                onClick={() => handleToggleSubdeck(subdeck._id)}
                                            >
                                                <div className="csm-subdeck-name">
                                                    {subdeck.name}
                                                </div>

                                                <div className="csm-subdeck-footer">
                                                    <span className="csm-subdeck-cards">
                                                        {subdeck.cardCount} cards
                                                    </span>

                                                    <div className="csm-subdeck-checkbox">
                                                        {isSelected && <Check size={14} />}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="csm-empty-state">
                            <div className="csm-empty-icon">
                                <Layers size={64} />
                            </div>
                            <p className="csm-empty-text">
                                Selecione um deck na barra lateral
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderItemCard = (item: StudyItem) => {
        const isSelected = selectedIds.includes(item._id);
        return (
            <div
                key={item._id}
                className={`csm-item-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleToggle(item._id)}
            >
                <div className="csm-item-checkbox">
                    {isSelected && <Check size={14} />}
                </div>
                <div className="csm-item-content">
                    <div className="csm-item-name">{item.name}</div>
                    <div className="csm-item-stats">
                        {item.cardCount} card{item.cardCount !== 1 ? 's' : ''}
                    </div>
                </div>
            </div>
        );
    };

    const renderSubdecksGrid = () => {
        if (mode !== 'subdecks') return null;

        const renderContent = () => {
            if (!groups || groups.length === 0) {
                return (
                    <div className="csm-items-grid">
                        {items.map(item => renderItemCard(item))}
                    </div>
                );
            }

            const itemsByGroup = new Map<string | null, StudyItem[]>();
            items.forEach(item => {
                const gId = item.groupId || null;
                if (!itemsByGroup.has(gId)) itemsByGroup.set(gId, []);
                itemsByGroup.get(gId)!.push(item);
            });

            return (
                <>
                    {groups.map(group => {
                        const groupItems = itemsByGroup.get(group._id) || [];
                        if (groupItems.length === 0) return null;
                        return (
                            <div key={group._id} className="csm-group-section">
                                <div className="csm-group-header">
                                    <span className="csm-group-dot" style={{ background: group.color || '#9CA3AF' }} />
                                    <span>{group.name}</span>
                                </div>
                                <div className="csm-items-grid">
                                    {groupItems.map(item => renderItemCard(item))}
                                </div>
                            </div>
                        );
                    })}
                    {(itemsByGroup.get(null) || []).length > 0 && (
                        <div className="csm-group-section">
                            <div className="csm-group-header">
                                <span className="csm-group-dot" style={{ background: '#9CA3AF' }} />
                                <span>Sem grupo</span>
                            </div>
                            <div className="csm-items-grid">
                                {(itemsByGroup.get(null) || []).map(item => renderItemCard(item))}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        return (
            <>
                <div className="csm-actions-top">
                    <div className="csm-actions-left">
                        <button className="csm-btn-select-all" onClick={handleSelectAll}>
                            <CheckSquare size={16} />
                            {selectedIds.length === items.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                        </button>
                    </div>
                    <span className="csm-selection-counter">
                        {selectedIds.length}/{items.length}
                    </span>
                </div>
                <div className="csm-content">
                    {renderContent()}
                </div>
            </>
        );
    };

    const getTotalSelected = () => {
        if (mode === 'subdecks') return selectedIds.length;
        return selectedSubdeckIds.size;
    };

    const getTotalDecksSelected = () => {
        if (mode !== 'decks') return 0;
        const decksWithSelection = new Set<string>();
        selectedSubdeckIds.forEach(subdeckId => {
            decksSubdecksMap.forEach((subdecks, deckId) => {
                if (subdecks.some(s => s._id === subdeckId)) {
                    decksWithSelection.add(deckId);
                }
            });
        });
        return decksWithSelection.size;
    };

    const hasSelection = getTotalSelected() > 0;

    return (
        <>
            <div className="csm-overlay" onClick={handleCloseAttempt}>
                <div className="csm-modal" onClick={(e) => e.stopPropagation()}>

                    <div className="csm-header">
                        <div className="csm-header-left">
                            <div className="csm-title-row">
                                <Target size={22} />
                                <h2>Estudo Personalizado</h2>
                            </div>

                            <p className="csm-subtitle">
                                Selecione os conteúdos de <strong>{contextName}</strong> para sua sessão de estudo
                            </p>
                        </div>

                        <button className="csm-btn-close" onClick={handleCloseAttempt}>
                            <X size={20} />
                        </button>
                    </div>

                    {loading ? (
                        <div className="csm-loading">
                            <div className="csm-spinner"></div>
                            <p>Carregando conteúdos</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="csm-empty-state">
                            <div className="csm-empty-icon">
                                <BookOpen size={64} />
                            </div>
                            <p className="csm-empty-text">Nenhum conteúdo disponível</p>
                        </div>
                    ) : (
                        <>
                            {mode === 'decks' ? renderSplitView() : renderSubdecksGrid()}
                        </>
                    )}
                </div>
            </div>

            {/* POWER-UP BAR */}
            <div className={`csm-powerup-bar ${hasSelection ? 'visible' : ''}`}>
                {mode === 'decks' && (
                    <>
                        <div className="csm-powerup-stat">
                            <div className="csm-powerup-icon">
                                <Layers size={24} />
                            </div>
                            <span className="csm-powerup-text">
                                <span className="csm-powerup-number">{getTotalDecksSelected()}</span> decks
                            </span>
                        </div>
                        <div className="csm-powerup-divider"></div>
                    </>
                )}

                <div className="csm-powerup-stat">
                    <div className="csm-powerup-icon">
                        <BookOpen size={24} />
                    </div>
                    <span className="csm-powerup-text">
                        <span className="csm-powerup-number">{getTotalSelected()}</span> subdecks
                    </span>
                </div>

                <div className="csm-powerup-divider"></div>

                <div className="csm-powerup-stat">
                    <div className="csm-powerup-icon">
                        <BarChart3 size={24} />
                    </div>
                    <span className="csm-powerup-text">
                        <span className="csm-powerup-number">{totalCards}</span> cards
                    </span>
                </div>

                <div className="csm-powerup-divider"></div>

                <button
                    className="csm-btn-powerup-start"
                    onClick={handleStartStudy}
                    disabled={!hasSelection}
                >
                    <Play size={16} />
                    Iniciar Estudo
                </button>
            </div>

            {/* CONFIRMAÇÃO DE SAÍDA */}
            {showExitConfirm && (
                <div className="csm-overlay" style={{ zIndex: 10000 }}>
                    <div
                        className="csm-modal csm-confirm-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="csm-header">
                            <div className="csm-header-content">
                                <div className="csm-header-icon">
                                    <AlertCircle size={32} />
                                </div>
                                <h2>Descartar Seleção</h2>
                            </div>
                        </div>

                        <div className="csm-confirm-content">
                            <p className="csm-confirm-text">
                                Você tem <strong style={{ color: 'var(--platform-primary)' }}>{getTotalSelected()} itens</strong> selecionados.
                                <br />
                                Deseja realmente sair e perder sua seleção?
                            </p>

                            <div className="csm-confirm-actions">
                                <button
                                    className="csm-btn-cancel"
                                    onClick={() => setShowExitConfirm(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="csm-btn-discard"
                                    onClick={handleConfirmExit}
                                >
                                    Descartar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomStudyModal;